import { useEffect, useState, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext({ logout: () => {} });
export const useAuth = () => useContext(AuthContext);

/**
 * Sperrt die gesamte App hinter einem Passwort. Die Sitzung liegt in einem
 * httpOnly-Cookie, das der Browser bei jeder /api-Anfrage automatisch mitschickt.
 */
export const PasswordGate = ({ children }) => {
  const [status, setStatus] = useState('checking'); // checking | locked | unlocked
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Laeuft irgendeine Anfrage in ein 401 (Sitzung abgelaufen oder auf einem
    // anderen Geraet abgemeldet), landet man automatisch wieder im Login.
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (err) => {
        if (err.response?.status === 401) {
          setStatus('locked');
        }
        return Promise.reject(err);
      }
    );

    axios
      .get(`${API}/auth/session`)
      .then((res) => setStatus(res.data.authenticated ? 'unlocked' : 'locked'))
      .catch(() => setStatus('locked'));

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Auch bei Fehler lokal sperren
    }
    setStatus('locked');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;

    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/auth/login`, { password });
      setPassword('');
      setStatus('unlocked');
    } catch (err) {
      setError(
        err.response?.status === 401
          ? 'Falsches Passwort'
          : 'Anmeldung fehlgeschlagen. Bitte später erneut versuchen.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (status === 'locked') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm" data-testid="login-screen">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold font-heading text-primary tracking-tight">
              STRONG CHIPTUNING
            </h1>
            <p className="text-xs text-muted-foreground font-subheading mt-1">
              Terminverwaltung
            </p>
          </div>

          <form
            onSubmit={handleLogin}
            className="bg-card border border-border rounded-lg p-6 space-y-4"
          >
            <div className="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-primary/10 border border-primary/20">
              <Lock className="w-5 h-5 text-primary" />
            </div>

            <Input
              id="password"
              data-testid="password-input"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              className="bg-secondary border-border focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground h-12 text-base text-center transition-all duration-200"
              placeholder="Passwort"
              autoFocus
              autoComplete="current-password"
            />

            {error && (
              <p className="text-destructive text-sm text-center" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-button"
              disabled={submitting || !password}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 text-base rounded transition-all duration-200"
            >
              {submitting ? 'Wird geprüft...' : 'Anmelden'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={{ logout }}>{children}</AuthContext.Provider>;
};
